import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useReducer,
} from "react";

export type ShippingMethodId =
  | "standard"
  | "standard_light"
  | "express"
  | "express_light"
  | "local_pickup"
  | "tma";

export type ShippingMethodOption = {
  id: ShippingMethodId;
  label: string;
  price: number;
  freeShippingPrice: number;
};

/** Val från Pulpo TMA checkout-widget */
export type DeliverySelection = {
  optionId: string;
  serviceCode: string;
  basicServiceCode?: string;
  type?: "home" | "service_point" | "parcel_locker" | "store_pickup";
  title: string;
  /** Baspris utan express-tillägg */
  basePriceOre?: number;
  basePrice?: number;
  priceOre: number;
  price: number;
  expressSelected?: boolean;
  expressSurchargeOre?: number;
  requiresServicePoint: boolean;
  servicePointType?: "service_point" | "locker" | null;
  additionalServiceCodes?: string[];
  servicePointId?: string;
  servicePointName?: string;
  servicePointStreet?: string;
  servicePointCity?: string;
  servicePointPostalCode?: string;
};

export const SHIPPING_METHODS: ShippingMethodOption[] = [
  { id: "standard", label: "Standard frakt", price: 99, freeShippingPrice: 0 },
  {
    id: "standard_light",
    label: "Standard Lätt",
    price: 59,
    freeShippingPrice: 0,
  },
  { id: "express", label: "Express frakt", price: 149, freeShippingPrice: 50 },
  {
    id: "express_light",
    label: "Express Frakt lätt",
    price: 109,
    freeShippingPrice: 50,
  },
  { id: "local_pickup", label: "Hämta i butik", price: 0, freeShippingPrice: 0 },
];

export const FREE_SHIPPING_THRESHOLD = 999;

/** WooCommerce shipping class räknas som "lätt frakt" (t.ex. slug "shipping-light" eller "lätt-frakt"). */
export function isShippingClassLight(shippingClass?: string): boolean {
  if (!shippingClass || typeof shippingClass !== "string") return false;
  const s = shippingClass.toLowerCase().trim();
  return (
    s === "shipping-light" ||
    s === "lätt-frakt" ||
    s === "latt-frakt" ||
    s.includes("light") ||
    s.includes("lätt")
  );
}

/**
 * Produkt får inte plats i paketbox — Core-attr `no_parcel_locker=1`
 * eller shipping_class `no-paketbox` / `no_parcel_locker` / `oversized` / `bulky`.
 */
export function isNoParcelLocker(opts: {
  no_parcel_locker?: boolean;
  shipping_class?: string;
}): boolean {
  if (opts.no_parcel_locker === true) return true;
  const s = (opts.shipping_class || "").toLowerCase().trim();
  if (!s) return false;
  return (
    s === "no-paketbox" ||
    s === "no_parcel_locker" ||
    s === "no-parcel-locker" ||
    s === "oversized" ||
    s === "bulky"
  );
}

export function getShippingPrice(
  methodId: ShippingMethodId,
  cartTotal: number,
  deliverySelection?: DeliverySelection | null
): number {
  if (deliverySelection && Number.isFinite(deliverySelection.price)) {
    return deliverySelection.price;
  }
  const method = SHIPPING_METHODS.find((m) => m.id === methodId);
  if (!method) return 0;
  return cartTotal >= FREE_SHIPPING_THRESHOLD
    ? method.freeShippingPrice
    : method.price;
}

export function isDeliverySelectionComplete(
  selection: DeliverySelection | null | undefined
): boolean {
  if (!selection?.optionId || !selection.serviceCode) return false;
  if (selection.requiresServicePoint && !selection.servicePointId) return false;
  return true;
}

export type Shipping = {
  first_name: string;
  last_name: string;
  company: ""; // alltid tom
  address_1: string;
  address_2: string;
  city: string;
  state: string;
  postcode: string;
  country: "SE"; // alltid Sweden
  email: string;
  phone: string;
};

type ShippingContextType = {
  shipping: Shipping;
  shippingMethod: ShippingMethodId;
  deliverySelection: DeliverySelection | null;
  setShippingMethod: (method: ShippingMethodId) => void;
  setDeliverySelection: (selection: DeliverySelection | null) => void;
  setShipping: (
    shipping: Partial<Omit<Shipping, "company" | "country">>
  ) => void;
  updateShippingField: (
    field: keyof Omit<Shipping, "company" | "country">,
    value: string
  ) => void;
  clearShipping: () => void;
};

const ShippingContext = createContext<ShippingContextType | undefined>(
  undefined
);

const initialShipping: Shipping = {
  first_name: "",
  last_name: "",
  company: "",
  address_1: "",
  address_2: "",
  city: "",
  state: "",
  postcode: "",
  country: "SE",
  email: "",
  phone: "",
};

type ShippingState = {
  address: Shipping;
  method: ShippingMethodId;
  deliverySelection: DeliverySelection | null;
};

const initialState: ShippingState = {
  address: initialShipping,
  method: "standard",
  deliverySelection: null,
};

type ShippingAction =
  | {
      type: "SET_SHIPPING";
      payload: Partial<Omit<Shipping, "company" | "country">>;
    }
  | {
      type: "UPDATE_FIELD";
      payload: {
        field: keyof Omit<Shipping, "company" | "country">;
        value: string;
      };
    }
  | { type: "CLEAR_SHIPPING" }
  | { type: "SET_METHOD"; payload: ShippingMethodId }
  | { type: "SET_DELIVERY"; payload: DeliverySelection | null };

const shippingReducer = (
  state: ShippingState,
  action: ShippingAction
): ShippingState => {
  switch (action.type) {
    case "SET_SHIPPING":
      return {
        ...state,
        address: {
          ...state.address,
          ...action.payload,
          company: "",
          country: "SE",
        },
      };

    case "UPDATE_FIELD":
      return {
        ...state,
        address: {
          ...state.address,
          [action.payload.field]: action.payload.value,
          company: "",
          country: "SE",
        },
      };

    case "CLEAR_SHIPPING":
      return { ...initialState };

    case "SET_METHOD":
      return { ...state, method: action.payload };

    case "SET_DELIVERY":
      return {
        ...state,
        deliverySelection: action.payload,
        method: action.payload ? "tma" : state.method,
      };

    default:
      return state;
  }
};

export const ShippingProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(shippingReducer, initialState);

  const setShipping = useCallback(
    (shippingData: Partial<Omit<Shipping, "company" | "country">>) => {
      dispatch({ type: "SET_SHIPPING", payload: shippingData });
    },
    []
  );

  const updateShippingField = useCallback(
    (
      field: keyof Omit<Shipping, "company" | "country">,
      value: string
    ) => {
      dispatch({ type: "UPDATE_FIELD", payload: { field, value } });
    },
    []
  );

  const clearShipping = useCallback(() => {
    dispatch({ type: "CLEAR_SHIPPING" });
  }, []);

  const setShippingMethod = useCallback((method: ShippingMethodId) => {
    dispatch({ type: "SET_METHOD", payload: method });
    if (method !== "tma") {
      dispatch({ type: "SET_DELIVERY", payload: null });
    }
  }, []);

  const setDeliverySelection = useCallback(
    (selection: DeliverySelection | null) => {
      dispatch({ type: "SET_DELIVERY", payload: selection });
    },
    []
  );

  const value = useMemo(
    () => ({
      shipping: state.address,
      shippingMethod: state.method,
      deliverySelection: state.deliverySelection,
      setShippingMethod,
      setDeliverySelection,
      setShipping,
      updateShippingField,
      clearShipping,
    }),
    [
      state.address,
      state.method,
      state.deliverySelection,
      setShippingMethod,
      setDeliverySelection,
      setShipping,
      updateShippingField,
      clearShipping,
    ]
  );

  return (
    <ShippingContext.Provider value={value}>
      {children}
    </ShippingContext.Provider>
  );
};

export const useShipping = () => {
  const context = useContext(ShippingContext);
  if (!context) {
    throw new Error("useShipping måste användas inom en ShippingProvider");
  }
  return context;
};
